import {
  Button,
  ButtonGroup,
  FormControl,
  FormLabel,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Select,
  Spinner,
} from '@apollo/orbit';
import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';

import {
  graphPersistedQueriesRouteConfig,
  variantPersistedQueriesRouteConfig,
} from 'src/app/graph/routes';
import { Form as FormWrapper } from 'src/components/common/form/Form';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { DocsRouter } from 'src/lib/routers';

import { usePQBatchLinkVariants } from '../../usePQBatchLinkVariants';
import { PersistedQueryListConfigHeader } from '../PersistedQueryListConfigHeader';
import {
  PersistedQueryListConfigStep,
  PersistedQueryListConfigWorkflow,
} from '../steps';

export interface PersistedQueryListLinkModalState {
  graphId?: string;
  listId?: string;
  name?: string;
  linkedVariantName?: string;
}

interface Props {
  isOpen: boolean;
  state: PersistedQueryListLinkModalState | null;
  onComplete: (linkedVariantId?: string, linkedVariantName?: string) => void;
  onClose: () => void;
}

export const PersistedQueryListLinkModal = ({
  isOpen,
  state,
  onComplete,
  onClose,
}: Props) => {
  // TODO(evan-pq): We don't need to perform this step if we are on a variant already since we know we want to link to it by default
  const { graphId: graphIdFromUrl } = useRouteParams(
    graphPersistedQueriesRouteConfig,
    variantPersistedQueriesRouteConfig,
  );
  const {
    allVariants,
    batchLinkVariants,
    loading,
    submitting,
    errors,
    failureMessages,
    unlinkResults,
    linkResults,
  } = usePQBatchLinkVariants({
    graphId: graphIdFromUrl,
    listId: state?.listId ?? '',
  });

  const [selectedVariantName, setSelectedVariantName] = useState<string>(
    state?.linkedVariantName || '',
  );

  const handleVariantChanged = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      setSelectedVariantName(event.target.value);
    },
    [setSelectedVariantName],
  );

  const handleSubmit = useCallback(async () => {
    if (!selectedVariantName) {
      // We are actively unlinking whatever is potentially linked.
      await batchLinkVariants(new Set());
    } else {
      // Only link the one that we have just selected.
      await batchLinkVariants(new Set([selectedVariantName]));
    }
  }, [selectedVariantName, batchLinkVariants]);

  useEffect(() => {
    if (!loading && !submitting && (linkResults || unlinkResults)) {
      // we've just run a link / unlink mutation
      if (errors.length > 0 || failureMessages.length > 0) {
        return;
      }
      const selectedVariantId = allVariants?.find(
        (v) => v.name === selectedVariantName,
      )?.id;
      onComplete(selectedVariantId || '', selectedVariantName || '');
    }
  }, [
    batchLinkVariants,
    onComplete,
    selectedVariantName,
    allVariants,
    linkResults,
    unlinkResults,
    loading,
    submitting,
    errors,
    failureMessages,
  ]);

  return (
    <Modal isOpen={isOpen} size="xl" onClose={onClose}>
      <ModalOverlay />
      <FormWrapper onSubmit={handleSubmit} noValidate>
        <ModalContent>
          <ModalHeader>
            <PersistedQueryListConfigHeader
              currentStep={PersistedQueryListConfigStep.Link}
              workflow={PersistedQueryListConfigWorkflow.create}
            />
            <ModalTitle>Optional: Link to a Variant</ModalTitle>
            <ModalDescription>
              Linking to a variant allows persisted query IDs from this list to
              be published to your router. Once linked, you will be able to use
              persisted query features for any linked variants.{' '}
              <Link isExternal to={DocsRouter.path('PersistedQueries')}>
                Learn more
              </Link>
            </ModalDescription>
          </ModalHeader>
          <ModalBody>
            {loading ? (
              <Spinner size="sm" />
            ) : (
              <>
                <FormControl>
                  <FormLabel htmlFor="variant">Variant</FormLabel>
                  <Select
                    isDisabled={loading || submitting}
                    name="variant"
                    value={selectedVariantName}
                    placeholder="Select a graph variant..."
                    onChange={handleVariantChanged}
                  >
                    {allVariants?.map((variant) => (
                      <option
                        key={variant.id}
                        value={variant.name}
                        data-testid={`option-${variant.name}`}
                      >
                        {variant.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                {errors.length > 0 && (
                  <ErrorMessage>
                    Something went wrong. Please refresh and try again.
                  </ErrorMessage>
                )}
                {failureMessages.length > 0 &&
                  failureMessages.map((fm) => (
                    <ErrorMessage key={fm.variantName}>
                      <p>{fm.message}</p>
                    </ErrorMessage>
                  ))}
              </>
            )}
          </ModalBody>
          <ModalFooter>
            {!selectedVariantName && (
              <Button
                variant="secondary"
                type="button"
                onClick={onClose}
                isDisabled={loading || submitting}
              >
                Cancel
              </Button>
            )}
            <ButtonGroup>
              {selectedVariantName ? (
                <Button
                  variant="secondary"
                  type="button"
                  onClick={onClose}
                  loadingText="Submitting"
                  isLoading={submitting}
                  isDisabled={loading || submitting}
                >
                  Cancel
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  onClick={(event) => {
                    event.preventDefault();
                    onComplete();
                  }}
                  isDisabled={loading || submitting}
                >
                  Skip
                </Button>
              )}
              <Button
                variant="primary"
                type="submit"
                loadingText="Linking"
                isLoading={submitting}
                isDisabled={!selectedVariantName || loading}
              >
                Link
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </FormWrapper>
    </Modal>
  );
};
